import { X } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * FeedCard (CHP-522) — the unified "feed / to-do" card.
 *
 * One card, one semantic lane carried by a 4px left edge (via `Card`'s
 * `accent`) plus a matching coloured eyebrow. Everything else stays neutral so
 * a stack of these reads calmly rather than shouting. This is the single
 * primitive the deal/manager feed sidebars converge onto (the bespoke
 * DealOutcomeCard / ExpiryNotificationCard / … are thin wrappers, CHP-523).
 *
 * Lane → colour:
 *   coral   = urgent / action required (pipeline emergency, expiring)
 *   amber   = needs attention soon (pending past SLA)
 *   red     = urgent / mandatory (outstanding compulsory training — CHP-877)
 *   success = positive / resolved
 *   info    = informational update
 *   neutral = low-priority / drafts
 */
export type FeedTone = "coral" | "amber" | "red" | "success" | "info" | "neutral";

const eyebrowColor: Record<FeedTone, string> = {
  coral: "text-primary",
  amber: "text-warning-muted-foreground",
  red: "text-destructive",
  success: "text-success",
  info: "text-info-muted-foreground",
  neutral: "text-muted-foreground",
};

export interface FeedCardProps {
  /** Semantic lane — paints the left edge and the eyebrow. Defaults to neutral. */
  tone?: FeedTone;
  /** Short uppercase category, e.g. "Pipeline emergency". Rendered as a micro-eyebrow. */
  eyebrow?: string;
  /** Optional leading lucide icon. */
  icon?: React.ComponentType<{ className?: string }>;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Right-aligned relative time, e.g. "2h ago". */
  timestamp?: string;
  /** Right-aligned action (a Button or link), or several. */
  action?: React.ReactNode;
  /**
   * Optional whole-card click — when set, the card becomes a `role="button"`
   * surface (keyboard-activatable, hover affordance) whose primary action is
   * `onClick`. The dismiss button and any nested action buttons stop
   * propagation so they don't double-fire. Omit for passive cards.
   */
  onClick?: () => void;
  /** Accessible name for the clickable card surface (required-ish when onClick is set). */
  ariaLabel?: string;
  /**
   * Optional dismiss [X] — renders a small top-right ghost icon-button.
   * Omit for non-dismissable cards (the affordance simply doesn't render).
   */
  onDismiss?: () => void;
  /** Accessible name for the dismiss [X]; should identify the card uniquely. */
  dismissLabel?: string;
  /** Optional data-testid for the dismiss [X] (raw — not prefixed). */
  dismissTestId?: string;
  className?: string;
  testId?: string;
  /** Pass a raw data-testid (un-prefixed) when a test expects an exact value. */
  rawTestId?: string;
}

export function FeedCard({
  tone = "neutral",
  eyebrow,
  icon: Icon,
  title,
  description,
  timestamp,
  action,
  onClick,
  ariaLabel,
  onDismiss,
  dismissLabel,
  dismissTestId,
  className,
  testId,
  rawTestId,
}: FeedCardProps) {
  const clickable = !!onClick;
  return (
    <Card
      accent={tone}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? ariaLabel : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              // CHP-805: only activate when the card surface itself is focused.
              // If the keydown bubbled up from a nested control (Dismiss /
              // Snooze / Review), calling preventDefault() here would cancel
              // that control's own native Enter/Space activation and open the
              // deal instead — hijacking the secondary action for keyboard users.
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        "relative flex items-start gap-4 p-4",
        clickable &&
          "cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        // Reserve space on the right edge for the dismiss [X] so it never
        // overlaps the action column.
        onDismiss && "pr-10",
        className,
      )}
      data-testid={rawTestId ?? (testId ? `feed-${testId}` : undefined)}
    >
      {onDismiss ? (
        <Button
          size="icon-sm"
          variant="ghost"
          className="absolute right-1.5 top-1.5 text-muted-foreground"
          aria-label={dismissLabel}
          data-testid={dismissTestId}
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
        >
          <X aria-hidden />
        </Button>
      ) : null}
      {Icon ? (
        <span className={cn("mt-0.5 shrink-0", eyebrowColor[tone])}>
          <Icon aria-hidden className="h-4 w-4" />
        </span>
      ) : null}
      <div className="min-w-0 flex-1 space-y-1">
        {(eyebrow || timestamp) && (
          <div className="flex items-center gap-2">
            {eyebrow ? (
              <span
                className={cn(
                  "font-mono text-2xs font-semibold uppercase tracking-[0.06em]",
                  eyebrowColor[tone],
                )}
              >
                {eyebrow}
              </span>
            ) : null}
            {timestamp ? (
              <span className="font-mono text-2xs text-muted-foreground">{timestamp}</span>
            ) : null}
          </div>
        )}
        <p className="text-sm font-semibold leading-snug text-foreground">{title}</p>
        {description ? <div className="text-sm text-muted-foreground">{description}</div> : null}
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </Card>
  );
}
